/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TermAndServiceModal } from "@src/components/TermsAndService/TermAndServiceModal";
import { useFlag } from "@unleash/proxy-client-react";
import { AppThunk, useAppDispatch } from "@src/config/store";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { LoginAction } from "@src/pages/login/ducks/slices";
import axios from "axios";
import {
  acceptUserEula,
  getLatestEula,
} from "@src/reducers/operations/eulaOperation";
import { sidebarAction } from "@src/reducers/slices/sidebarSlice";
import { SignUpAction } from "@src/pages/signup/ducks/slices";

interface TermAndServicePageProps {
  isPublicPage?: boolean;
}

const TermAndServicePage: FC<TermAndServicePageProps> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location]
  );

  const { isAuthenticated } = useSelector(
    (state: IRootState) => state.loginReducer
  );
  const { acceptEula } = useSelector((state: IRootState) => state.eulaReducer);
  const { isMobile } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );

  const logoutWithoutReloading = (): AppThunk => async () => {
    try {
      await axios.post(`/auth/logout`);
      dispatch(LoginAction.clearAuthentication({ isReloadBrowser: false }));
    } catch (err) {}
  };

  // Hide Sidebar for mobile devices and show EULA
  useEffect(() => {
    const shouldHideSidebarWithTermsAndServicePage =
      isAuthenticated && !acceptEula && isMobile;
    if (shouldHideSidebarWithTermsAndServicePage) {
      dispatch(sidebarAction.expandUI(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, acceptEula, isMobile]);

  const showTermsAndServiceModal = useMemo(() => {
    return (
      (isAuthenticated ||
        location.pathname.includes("/public/terms-of-service")) &&
      !acceptEula
    );
  }, [isAuthenticated, acceptEula, location]);

  useEffect(() => {
    if (location.pathname.includes("/public/terms-of-service")) {
      dispatch(getLatestEula());
      dispatch(SignUpAction.showSignUpEulaAgreementInfo(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  return (
    <div className="content-wrapper">
      <div className="content">
        {showTermsAndServiceModal && (
          <TermAndServiceModal
            isPublic={location.pathname.includes("/public/terms-of-service")}
            t={t}
            acceptAction={() => {
              dispatch(acceptUserEula());
              setTimeout(() => {
                navigate("/");
              }, 500);
            }}
            closeAction={() => {
              if (location.pathname.includes("/public/terms-of-service")) {
                window.location.href = "/";
              } else {
                dispatch(logoutWithoutReloading());
                setTimeout(() => {
                  window.location.href = "/?eulaWarnMessage=true";
                }, 500);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export { TermAndServicePage };
